import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { RefreshCw, Wifi, WifiOff, AlertTriangle, Radio } from "lucide-react";

// Map the backend `ws_status` (Coach private-WS lifecycle) to a
// user-facing live-connection label for the Trades page.
function statusMeta(ws) {
  switch (ws) {
    case "live":
      return { label: "Live", tone: "var(--bt-win)", Icon: Wifi, pulse: true,
        hint: "Streaming fills in real time" };
    case "connecting":
    case "launching":
    case "reconciling":
      return { label: "Reconnecting", tone: "var(--bt-warn)", Icon: Radio, pulse: true,
        hint: "Re-establishing the live connection" };
    case "degraded_polling":
    case "giving_up_degraded":
      return { label: "REST fallback", tone: "var(--bt-warn)", Icon: RefreshCw, pulse: false,
        hint: "Live socket unavailable — syncing via REST safety net" };
    case "auth_failed":
      return { label: "Auth error", tone: "var(--bt-loss)", Icon: AlertTriangle, pulse: false,
        hint: "Delta rejected the API key — reconnect it in settings" };
    default:
      return { label: "Offline", tone: "var(--bt-text-muted)", Icon: WifiOff, pulse: false,
        hint: "Not streaming — use Sync now or wait for the periodic sync" };
  }
}

function timeAgo(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export const DeltaSyncBar = ({ onSynced }) => {
  const [conn, setConn] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const pollRef = useRef(null);

  const fetchConn = useCallback(async () => {
    try {
      const { data } = await api.get("/delta-connection");
      setConn(data && data.status !== "not_connected" ? data : null);
    } catch (e) {
      setConn(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchConn();
    pollRef.current = setInterval(fetchConn, 15000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [fetchConn]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/delta-connection/sync-now");
      if (data.status === "ok") {
        const log = data.sync_log || {};
        toast.success(
          `Synced · ${log.fills_inserted ?? 0} new fill(s) · ` +
          `${log.final_open_positions ?? 0} open · ${log.final_closed_trades ?? 0} closed`
        );
        await fetchConn();
        onSynced && onSynced();
      } else if (data.status === "rate_limited") {
        toast.info(data.message || "Please wait a moment before syncing again.");
      } else if (data.status === "locked") {
        toast.info(data.message || "A sync is already running.");
      } else {
        toast.error(data.message || "Sync failed.");
      }
    } catch (e) {
      toast.error("Sync failed. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  if (!loaded || !conn) return null;

  const meta = statusMeta(conn.ws_status);
  const Icon = meta.Icon;
  const log = conn.last_sync_log || null;
  const lastSynced = timeAgo(conn.last_synced_at || (log && log.at));

  return (
    <div
      data-testid="delta-sync-bar"
      className="rounded-lg border border-[var(--bt-border)] bg-[var(--bt-surface)]/60 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
    >
      <div className="flex items-center gap-3">
        <span
          data-testid="delta-ws-status"
          data-ws-status={conn.ws_status || "offline"}
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide"
          style={{ background: `color-mix(in srgb, ${meta.tone} 15%, transparent)`, color: meta.tone }}
          title={meta.hint}
        >
          <span className="relative flex h-2 w-2">
            {meta.pulse && (
              <span
                className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping"
                style={{ background: meta.tone }}
              />
            )}
            <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: meta.tone }} />
          </span>
          <Icon className="w-3.5 h-3.5" /> {meta.label}
        </span>
        <div className="text-xs text-[var(--bt-text-muted)]">
          <span className="font-semibold text-[var(--bt-text)]">Delta Auto-sync</span>
          {conn.api_key_masked && <span className="ml-1 font-mono">{conn.api_key_masked}</span>}
          {lastSynced && (
            <span className="ml-2" data-testid="delta-last-synced">· last sync {lastSynced}</span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {log && (
          <span
            data-testid="delta-sync-log-summary"
            className="hidden md:inline text-[11px] font-mono text-[var(--bt-text-muted)]"
            title={JSON.stringify(log, null, 2)}
          >
            {log.endpoint} · {log.pages}p · {log.raw_fills} raw · {log.deduped} deduped
            {log.rejected ? ` · ${log.rejected} rejected` : ""} · {log.fills_inserted} new
          </span>
        )}
        <Button
          onClick={syncNow}
          disabled={syncing}
          data-testid="delta-sync-now-btn"
          variant="outline"
          className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] text-white rounded-sm"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing…" : "Sync now"}
        </Button>
      </div>
    </div>
  );
};

export default DeltaSyncBar;
